/* Onboarding, offboarding, EOSB, government, Saudization, misc lists, approvals, notifications. */
import { absenceReportBlocked, feeCalc, GOV_TYPES, nitaqat, trialLeft } from "../../domain/compliance";
import { evResignationAcknowledged, evResignationSubmitted, evSettlementReady } from "./events";
import { isISODate } from "../../domain/dates";
import { eosb, settlement } from "../../domain/payroll";
import type * as T from "../types";
import { E, isInt, isNum, str, type Ctx } from "./ctx";
import { approvals, expiringList, loansSorted } from "./derived";

export const OFFBOARDING_TYPES = ["Resignation", "Termination", "Contract end", "Probation not confirmed", "Retirement"];
export const CANDIDATE_STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];

const index = (i: number, max: number) => {
  if (!Number.isInteger(i) || i < 0 || i > max) throw E.field("i", `must be an integer between 0 and ${max}`);
  return i;
};
const hires = (v: number | undefined) => (isNum(v) ? Math.max(0, Math.floor(v)) : 0);

function runStatus(r: T.PayrollRun): string {
  if (r.paid) return "paid";
  if (r.mudadAck) return "Mudad acknowledged — ready to pay";
  if (r.exported) return "WPS file exported — awaiting Mudad";
  if (r.approved) return "approved — ready to export";
  return `draft (step ${r.step} of 4)`;
}

export function lifecycleOps(c: Ctx) {
  const offboarding = (id: number) => {
    const o = c.state.offboarding.find((x) => x.id === id);
    if (!o) throw E.notFound("offboarding record not found");
    return o;
  };
  const notifications = (u: T.User): T.Notification[] => {
    const now = c.now;
    const emp = c.isEmployee(u);
    const only = emp ? c.ownEid(u) : undefined;
    const out: Omit<T.Notification, "read">[] = [];
    const docs = expiringList(c, 30, only);
    out.push({ id: "n-docs", at: now, title: "Documents expiring", body: `${docs.length} documents expire within 30 days`, route: "/documents", tone: docs.length ? "warn" : "ok" });
    if (!emp) {
      const aps = approvals(c);
      out.push({ id: "n-approvals", at: now, title: "Pending approvals", body: `${aps.length} requests waiting for a decision`, route: "/approvals", tone: aps.length ? "info" : "ok" });
    }
    const r = c.state.run;
    out.push({ id: "n-payroll", at: now, title: "Payroll " + r.period, body: "Payroll run is " + runStatus(r), route: "/payroll", tone: r.paid ? "ok" : "info" });
    if (!emp) {
      const sub = c.state.subscription;
      const left = trialLeft(sub, c.today);
      if (sub.status === "active") out.push({ id: "n-trial", at: now, route: "/billing", title: "Subscription active", body: `Your ${sub.plan} plan is active until ${sub.activeUntil ?? ""}`, tone: "ok" });
      else if (sub.status === "pending") out.push({ id: "n-trial", at: now, route: "/billing", title: "Payment under review", body: `We are verifying your transfer · ${left} trial days left`, tone: "info" });
      else out.push({ id: "n-trial", at: now, route: "/billing", title: "Trial", body: `${left} days left in your free trial`, tone: left === 0 ? "bad" : "warn" });
    }
    const reads = c.state.notifReads[u.id] ?? {};
    return out.map((n) => ({ ...n, read: reads[n.id] === n.title + "\u0000" + n.body }));
  };

  return {
    async listOnboarding(): Promise<T.Onboarding[]> {
      c.principal("hr.modules");
      return c.state.onboarding.slice().sort((a, b) => a.employeeId - b.employeeId);
    },

    async toggleOnboardingTask(employeeId: number, i: number): Promise<T.Onboarding> {
      const u = c.principal("hr.modules");
      index(i, 7);
      const ob = c.state.onboarding.find((x) => x.employeeId === employeeId);
      if (!ob) throw E.notFound("onboarding record not found");
      while (ob.tasks.length < 8) ob.tasks.push(0);
      ob.tasks[i] = 1 - ob.tasks[i];
      c.audit(u, "Toggled onboarding task", "Onboarding", String(employeeId), `task ${i} → ${ob.tasks[i]}`);
      return ob;
    },

    async listOffboarding(): Promise<T.Offboarding[]> {
      c.principal("hr.modules");
      return c.state.offboarding.slice().sort((a, b) => a.id - b.id);
    },

    async createOffboarding(input: T.OffboardingInput): Promise<T.Offboarding> {
      const u = c.principal("hr.modules");
      const fe: Record<string, string> = {};
      if (!isInt(input?.employeeId)) fe.employeeId = "required";
      if (!OFFBOARDING_TYPES.includes(str(input?.type))) fe.type = "invalid offboarding type";
      const lastDay = str(input?.lastDay);
      if (!isISODate(lastDay)) fe.lastDay = "valid date required";
      else if (lastDay < c.today) fe.lastDay = "cannot be in the past";
      if (Object.keys(fe).length) throw E.validation(fe);
      c.emp(input.employeeId);
      if (c.state.offboarding.some((o) => o.employeeId === input.employeeId && o.status !== "Settled")) throw E.conflict("employee already has an open offboarding record");
      const id = c.nextId("offboarding", 801, () => c.state.offboarding.reduce((m, x) => Math.max(m, x.id), 0));
      const o: T.Offboarding = { id, employeeId: input.employeeId, type: input.type, submitted: c.today, lastDay, status: input.type === "Resignation" ? "Notice period" : "Clearance", clearance: [0, 0, 0, 0, 0, 0], acknowledged: false };
      c.state.offboarding.push(o);
      c.audit(u, "Started offboarding", "Offboarding", String(id), `${o.type} for employee ${o.employeeId}, last day ${lastDay}`);
      if (o.type === "Resignation") evResignationSubmitted(c, o, u.id);
      return o;
    },

    async toggleClearance(id: number, i: number): Promise<T.Offboarding> {
      const u = c.principal("hr.modules");
      const o = offboarding(id);
      index(i, 5);
      if (o.status === "Settled") throw E.conflict("offboarding already settled");
      while (o.clearance.length < 6) o.clearance.push(0);
      o.clearance[i] = 1 - o.clearance[i];
      const all = o.clearance.slice(0, 6).every((x) => x === 1);
      if (all) {
        o.status = "Settled";
        c.emp(o.employeeId).status = "Terminated";
      }
      c.audit(u, "Updated clearance", "Offboarding", String(o.id), `item ${i} → ${o.clearance[i]}`);
      if (all) {
        c.audit(u, "Terminated employee", "Employee", String(o.employeeId), "final settlement completed");
        const e = c.emp(o.employeeId);
        evSettlementReady(c, o, settlement(e, o.lastDay, o.type, loansSorted(c), c.today, c.country).net);
      }
      return o;
    },

    async acknowledgeResignation(id: number): Promise<T.Offboarding> {
      const u = c.principal("hr.modules");
      const o = offboarding(id);
      if (o.acknowledged) throw E.conflict("already acknowledged");
      o.acknowledged = true;
      c.audit(u, "Acknowledged resignation", "Offboarding", String(o.id), "");
      if (o.type === "Resignation") evResignationAcknowledged(c, o);
      return o;
    },

    async getSettlement(id: number): Promise<T.Settlement> {
      c.principal("hr.modules");
      const o = offboarding(id);
      return settlement(c.emp(o.employeeId), o.lastDay, o.type, loansSorted(c), c.today, c.country);
    },

    async eosbQuote(employeeId: number, end: string, reason: string): Promise<T.EosbResult> {
      c.principal("hr.modules");
      if (!isInt(employeeId)) throw E.field("employeeId", "must be an integer");
      const e2 = end || c.today;
      if (!isISODate(e2)) throw E.field("end", "valid date required");
      return eosb(c.emp(employeeId), e2, reason || "termination", 0, c.country);
    },

    async listGovTransactions(): Promise<T.GovTransaction[]> {
      c.principal("hr.modules");
      return c.state.gov.slice().sort((a, b) => b.id - a.id);
    },

    async createGovTransaction(input: T.GovTransactionInput): Promise<T.GovTransaction> {
      const u = c.principal("hr.modules");
      const fe: Record<string, string> = {};
      if (!isInt(input?.employeeId)) fe.employeeId = "required";
      if (!(GOV_TYPES as readonly string[]).includes(str(input?.type))) fe.type = "invalid transaction type";
      const period = input?.period == null ? 0 : input.period;
      if (!isInt(period) || period < 0 || period > 60) fe.period = "must be an integer between 0 and 60";
      if (Object.keys(fe).length) throw E.validation(fe);
      const e = c.emp(input.employeeId);
      if (input.type === "Absence report (Huroob)" && absenceReportBlocked(e, c.today)) throw E.conflict("absence report blocked: Iqama has fewer than 60 days left");
      const id = c.nextId("gov", 4401, () => c.state.gov.reduce((m, x) => Math.max(m, x.id), 0));
      const g: T.GovTransaction = { id, employeeId: e.id, type: input.type, period, fee: feeCalc(input.type, period).total, status: "Awaiting approval", date: c.today, ref: "—" };
      c.state.gov.push(g);
      c.audit(u, "Created government transaction", "GovTransaction", String(id), `${g.type} for employee ${g.employeeId}`);
      return g;
    },

    async govFee(type: string, months: number): Promise<T.FeeResult> {
      c.principal("gov.fees");
      const m = months == null ? 0 : months;
      if (!isInt(m) || m < 0 || m > 60) throw E.field("months", "must be an integer between 0 and 60");
      return feeCalc(type || "", m);
    },

    async saudization(saudiHires?: number, nonSaudiHires?: number): Promise<T.NitaqatResult> {
      c.principal("hr.modules");
      return nitaqat(c.employeesSorted(), hires(saudiHires), hires(nonSaudiHires));
    },

    async authenticatePending(): Promise<T.NitaqatResult> {
      const u = c.principal("hr.modules");
      let n = 0;
      for (const e of c.employeesSorted()) {
        if (e.nationality === "SA" && e.qiwa.status !== "Authenticated") { e.qiwa.status = "Authenticated"; n++; }
      }
      c.audit(u, "Authenticated Qiwa contracts", "Employee", "", `${n} contracts`);
      return nitaqat(c.employeesSorted(), 0, 0);
    },

    async listCases(): Promise<T.Case[]> { c.principal("hr.modules"); return c.state.cases.slice().sort((a, b) => a.id - b.id); },
    async listCandidates(): Promise<T.Candidate[]> { c.principal("hr.modules"); return c.state.candidates.slice().sort((a, b) => a.id - b.id); },

    async moveCandidate(id: number, stage: string): Promise<T.Candidate> {
      const u = c.principal("hr.modules");
      if (!CANDIDATE_STAGES.includes(stage)) throw E.field("stage", "must be one of " + CANDIDATE_STAGES.join(", "));
      const cand = c.state.candidates.find((x) => x.id === id);
      if (!cand) throw E.notFound("candidate not found");
      const from = cand.stage;
      cand.stage = stage;
      c.audit(u, "Moved candidate", "Candidate", String(id), from + " → " + stage);
      return cand;
    },

    async listGoals(): Promise<T.Goal[]> { c.principal("hr.modules"); return c.state.goals.slice().sort((a, b) => a.id - b.id); },

    async listAudit(): Promise<T.AuditEntry[]> {
      c.principal("audit");
      return c.state.audit.slice().sort((a, b) => (a.at !== b.at ? (a.at < b.at ? 1 : -1) : a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
    },

    async listApprovals(): Promise<T.Approval[]> {
      c.principal("approvals");
      return approvals(c);
    },

    async listNotifications(): Promise<T.Notification[]> {
      return notifications(c.principal("notifications"));
    },

    async markNotificationsRead(): Promise<void> {
      const u = c.principal("notifications");
      const doc: Record<string, string> = {};
      for (const n of notifications(u)) doc[n.id] = n.title + "\u0000" + n.body;
      c.state.notifReads[u.id] = doc;
    },
  };
}
